import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, ChevronRight, BanknoteIcon, CreditCard, Landmark, CheckSquare, DollarSign, Tag, FileText, Calendar } from 'lucide-react';
import Button from '../ui/Button';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { Expense } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { getCurrentArgentinaISOString, formatArgentinaDate } from '../../utils/dateUtils';

const PAYMENT_METHODS = [
  { id: 'cash' as const, label: 'Efectivo', icon: BanknoteIcon },
  { id: 'transfer' as const, label: 'Transferencia', icon: Landmark },
  { id: 'card' as const, label: 'Tarjeta', icon: CreditCard },
  { id: 'mercadopago' as const, label: 'MercadoPago', icon: DollarSign },
  { id: 'check' as const, label: 'Cheque', icon: CheckSquare },
];

const VAT_OPTIONS = [
  { value: 0, label: 'No gravado' },
  { value: 10.5, label: '10.5%' },
  { value: 21, label: '21%' },
];

const RECURRENCE_OPTIONS = [
  { value: 'daily' as const, label: 'Diaria' },
  { value: 'weekly' as const, label: 'Semanal' },
  { value: 'monthly' as const, label: 'Mensual' },
  { value: 'quarterly' as const, label: 'Trimestral' },
  { value: 'yearly' as const, label: 'Anual' },
];

const formatDateShort = (d: string) => {
  if (!d) return '';
  return formatArgentinaDate(d, "d 'de' MMM");
};

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (expense: Omit<Expense, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  initialData?: Expense;
}

type WizardStep = 1 | 2;

const ExpenseModal: React.FC<ExpenseModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const { expenseCategories, fetchExpenseCategories } = useAppStore();
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');

  const [formData, setFormData] = useState<Omit<Expense, 'id' | 'created_at' | 'updated_at'>>({
    supplier_id: undefined,
    category_id: '',
    description: '',
    amount: 0,
    vat: 0,
    payment_method: 'cash',
    date: getCurrentArgentinaISOString(),
    is_recurring: false,
    recurrence: undefined,
  });

  useEffect(() => {
    if (isOpen) {
      fetchExpenseCategories();
      setCurrentStep(1);
      setError(null);
      setCategorySearchQuery('');
    }
  }, [isOpen, fetchExpenseCategories]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        supplier_id: initialData.supplier_id,
        category_id: initialData.category_id,
        description: initialData.description,
        amount: initialData.amount,
        vat: initialData.vat || 0,
        payment_method: initialData.payment_method,
        date: initialData.date,
        is_recurring: initialData.is_recurring,
        recurrence: initialData.recurrence,
        parent_id: initialData.parent_id,
      });
    } else {
      setFormData({
        supplier_id: undefined,
        category_id: '',
        description: '',
        amount: 0,
        vat: 0,
        payment_method: 'cash',
        date: getCurrentArgentinaISOString(),
        is_recurring: false,
        recurrence: undefined,
      });
    }
  }, [initialData, isOpen]);

  const step1Valid = !!formData.category_id && !!formData.description?.trim() && formData.amount > 0;

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data, err } = await supabase.from('expenses_categories').insert([{ name: newCategoryName.trim() }]).select().single();
      if (err) throw err;
      await fetchExpenseCategories();
      setFormData((p) => ({ ...p, category_id: data.id }));
      setNewCategoryName('');
      setShowAddCategory(false);
    } catch (e: any) {
      setError(e.message || 'Error al crear categoría');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!initialData && currentStep === 1) return;
    if (!formData.category_id || !formData.description?.trim() || formData.amount <= 0) return;
    if (formData.is_recurring && !formData.recurrence) {
      setError('Seleccioná la recurrencia');
      return;
    }
    try {
      setIsLoading(true);
      await onSave(formData as any);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Error al guardar');
    } finally {
      setIsLoading(false);
    }
  };

  const categoryName = expenseCategories.find((c) => c.id === formData.category_id)?.name || '';

  // Modo edición: formulario único
  if (initialData) {
    return (
      <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
          <Dialog.Content
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
            onInteractOutside={(e) => {
              const target = e.target as HTMLElement;
              if (target.closest('[data-radix-select-viewport], [role="listbox"], [role="option"], [data-radix-popper-content-wrapper]')) {
                e.preventDefault();
              }
            }}
          >
            <div className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden animate-fade-in">
              <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700">
                <Dialog.Title asChild>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Editar gasto</h2>
                </Dialog.Title>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div>
                  <Select
                    label="Categoría"
                    value={formData.category_id}
                    onChange={(v) => setFormData((p) => ({ ...p, category_id: v }))}
                    options={expenseCategories.map((c) => ({ value: c.id, label: c.name, searchText: c.name }))}
                    searchable
                    searchQuery={categorySearchQuery}
                    onSearchChange={setCategorySearchQuery}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descripción</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                    placeholder="¿En qué se gastó?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Monto</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium">$</span>
                    <input
                      type="number"
                      value={formData.amount || ''}
                      onChange={(e) => setFormData((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                      min="0"
                      step="0.01"
                      className="w-full h-12 pl-9 pr-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-lg font-semibold"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">IVA</label>
                    <select
                      value={formData.vat}
                      onChange={(e) => setFormData((p) => ({ ...p, vat: parseFloat(e.target.value) }))}
                      className="w-full h-12 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {VAT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Fecha</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData((p) => ({ ...p, date: e.target.value }))}
                      className="w-full h-12 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Método de pago</label>
                  <div className="flex flex-wrap gap-2">
                    {PAYMENT_METHODS.map((pm) => {
                      const Icon = pm.icon;
                      return (
                        <button
                          key={pm.id}
                          type="button"
                          onClick={() => setFormData((p) => ({ ...p, payment_method: pm.id }))}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                            formData.payment_method === pm.id
                              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                              : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          <span className="text-sm font-medium">{pm.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="rec"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData((p) => ({ ...p, is_recurring: e.target.checked, recurrence: e.target.checked ? p.recurrence : undefined }))}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="rec" className="text-sm text-gray-700 dark:text-gray-300">Gasto recurrente</label>
                </div>
                {formData.is_recurring && (
                  <select
                    value={formData.recurrence || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, recurrence: e.target.value as any }))}
                    className="w-full h-12 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Seleccionar...</option>
                    {RECURRENCE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                )}
                {error && <p className="text-sm text-red-500">{error}</p>}
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                  <Button type="submit" disabled={isLoading} className="flex-1">{isLoading ? 'Guardando...' : 'Guardar'}</Button>
                </div>
              </form>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  // Modo nuevo: wizard 2 pasos
  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
        <Dialog.Content
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest('[data-radix-select-viewport], [role="listbox"], [role="option"], [data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }}
        >
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden animate-fade-in">
            {/* Header */}
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between mb-4">
                <Dialog.Title asChild>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Nuevo gasto</h2>
                </Dialog.Title>
                <button type="button" onClick={onClose} className="p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex gap-2">
                <div className={`flex-1 h-1.5 rounded-full ${currentStep >= 1 ? 'bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
                <div className={`flex-1 h-1.5 rounded-full ${currentStep >= 2 ? 'bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Paso {currentStep} de 2</p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (currentStep === 1 && step1Valid) {
                  setCurrentStep(2);
                  return;
                }
                if (currentStep === 2) {
                  handleSubmit(e);
                }
              }}
              className="px-6 pb-6"
            >
              {currentStep === 1 && (
                <div className="space-y-5">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <Tag className="h-4 w-4 text-indigo-500" />
                      ¿Qué categoría?
                    </p>
                    <div className="flex gap-2 items-center">
                      <div className="flex-1 min-w-0">
                        <Select
                          label=""
                          value={formData.category_id}
                          onChange={(v) => setFormData((p) => ({ ...p, category_id: v }))}
                          options={expenseCategories.map((c) => ({ value: c.id, label: c.name, searchText: c.name }))}
                          searchable
                          searchQuery={categorySearchQuery}
                          onSearchChange={setCategorySearchQuery}
                          className="mb-0"
                          wrapperClassName="mb-0"
                        />
                      </div>
                      {showAddCategory ? (
                        <div className="flex gap-2 flex-shrink-0 items-end">
                          <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="Nueva"
                            className="h-12 px-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm w-28"
                            autoFocus
                          />
                          <Button type="button" size="sm" onClick={handleAddCategory} disabled={!newCategoryName.trim()}>Crear</Button>
                          <button type="button" onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }} className="text-gray-500 p-2">✕</button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAddCategory(true)}
                          icon={<Plus className="h-4 w-4" />}
                          className="flex-shrink-0 self-end"
                        >
                          Nueva
                        </Button>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-indigo-500" />
                      ¿Cuánto?
                    </p>
                    <div className="relative">
                      <span className="absolute left-5 top-1/2 -translate-y-1/2 text-2xl font-semibold text-gray-400 dark:text-gray-500">$</span>
                      <input
                        type="number"
                        value={formData.amount || ''}
                        onChange={(e) => setFormData((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                        min="0"
                        step="0.01"
                        className="w-full h-16 pl-12 pr-5 rounded-2xl border-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-2xl font-bold text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                      />
                    </div>
                    {formData.amount > 0 && (
                      <p className="mt-1.5 text-sm text-gray-500">{formatCurrency(formData.amount)}</p>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-indigo-500" />
                      Descripción
                      <span className="text-red-500">*</span>
                    </p>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                      placeholder="Ej: Compra en supermercado"
                      className="w-full h-12 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400"
                    />
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-5">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-indigo-500" />
                      Método de pago
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {PAYMENT_METHODS.map((pm) => {
                        const Icon = pm.icon;
                        return (
                          <button
                            key={pm.id}
                            type="button"
                            onClick={() => setFormData((p) => ({ ...p, payment_method: pm.id }))}
                            className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all ${
                              formData.payment_method === pm.id
                                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            <Icon className="h-4 w-4" />
                            <span className="text-sm font-medium">{pm.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">IVA</p>
                      <select
                        value={formData.vat}
                        onChange={(e) => setFormData((p) => ({ ...p, vat: parseFloat(e.target.value) }))}
                        className="w-full h-12 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {VAT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Fecha
                      </p>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData((p) => ({ ...p, date: e.target.value }))}
                        className="w-full h-12 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="rec-wiz"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData((p) => ({ ...p, is_recurring: e.target.checked, recurrence: e.target.checked ? p.recurrence : undefined }))}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="rec-wiz" className="text-sm text-gray-700 dark:text-gray-300">Es recurrente</label>
                  </div>
                  {formData.is_recurring && (
                    <select
                      value={formData.recurrence || ''}
                      onChange={(e) => setFormData((p) => ({ ...p, recurrence: e.target.value as any }))}
                      className="w-full h-12 px-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">Seleccionar...</option>
                      {RECURRENCE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  )}

                  {/* Resumen compacto */}
                  <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-4 border border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Resumen</p>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{categoryName || '—'}</p>
                        <p className="text-sm text-gray-500">{formData.description || 'Sin descripción'}</p>
                      </div>
                      <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400">{formatCurrency(formData.amount)}</p>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{formatDateShort(formData.date)} · {PAYMENT_METHODS.find(p => p.id === formData.payment_method)?.label}</p>
                  </div>
                </div>
              )}

              {error && <p className="text-sm text-red-500 mt-3">{error}</p>}

              <div className="flex gap-3 mt-6">
                {currentStep === 1 ? (
                  <>
                    <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      disabled={!step1Valid}
                      className="flex-1 flex items-center justify-center gap-1 px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                    >
                      Siguiente
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(1)} className="flex-1">Atrás</Button>
                    <Button type="submit" disabled={isLoading || !step1Valid} className="flex-1">
                      {isLoading ? 'Guardando...' : `Crear gasto · ${formatCurrency(formData.amount)}`}
                    </Button>
                  </>
                )}
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ExpenseModal;
